import { createAsyncThunk } from '@reduxjs/toolkit';
import { QueryClient } from 'react-query';
import { CaseByIdQuery, useUpdateCaseAttributesMutation } from 'src/graphql';
import { TRootState } from 'src/store';
import { DateType } from 'src/types';

type CaseDateUpdatedArgs = {
  caseId: string;
  dateType: DateType;
  date: string | null;
  mutateUpdateCaseAttributes: ReturnType<
    typeof useUpdateCaseAttributesMutation
  >['mutateAsync'];
  queryClient: QueryClient;
};

export const caseDateUpdated = createAsyncThunk<
  void,
  CaseDateUpdatedArgs,
  { state: TRootState }
>(
  'case/dateUpdated',
  async (
    {
      caseId,
      dateType,
      date,
      mutateUpdateCaseAttributes,
      queryClient,
    }: CaseDateUpdatedArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const existingAttributes = state.case.caseById?.CaseAttributes;

    const dateObj = date ? new Date(date) : null;

    const queryKey = ['CaseById', { id: caseId }];
    queryClient.cancelQueries(queryKey);
    queryClient.setQueryData<CaseByIdQuery | undefined>(queryKey, (oldData) => {
      if (!oldData?.caseById) return oldData;

      return {
        ...oldData,
        caseById: {
          ...oldData.caseById,
          CaseAttributes: {
            ...oldData.caseById.CaseAttributes,
            [dateType]: dateObj,
          },
        },
      };
    });

    try {
      await mutateUpdateCaseAttributes({
        caseAttributes: {
          caseId,
          ...existingAttributes,
          [dateType]: dateObj,
        },
      });
    } catch (e) {
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        queryKey,
        (oldData) => {
          if (!oldData?.caseById) return oldData;

          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              CaseAttributes: existingAttributes
                ? {
                    ...existingAttributes,
                  }
                : undefined,
            },
          };
        },
      );
    } finally {
      queryClient.invalidateQueries(queryKey);
    }
  },
);
